import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  GraduationCap, ArrowLeft, Play, Check, Clock, Target,
  BookOpen, Filter, Search, Lock, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VirtualSchool() {
  const { courseId } = useParams();
  const { user } = useAuth();
  
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [myLearning, setMyLearning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ tier: 'all', level: 'all' });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCourses();
    if (user) fetchMyLearning();
  }, [user]);

  useEffect(() => {
    if (courseId && courses.length) {
      const course = courses.find(c => c.id === courseId);
      setSelectedCourse(course);
    }
  }, [courseId, courses]);

  const fetchCourses = async () => {
    try {
      const res = await fetch(`${API}/api/foundation/courses`);
      if (res.ok) {
        const data = await res.json();
        setCourses(data.courses || []);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyLearning = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/foundation/my-learning`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setMyLearning(await res.json());
      }
    } catch (err) {
      console.error('Error fetching my learning:', err);
    }
  };

  const enrollInCourse = async (courseId) => {
    if (!user) {
      toast.error('Please log in to enroll in courses');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/foundation/courses/enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ course_id: courseId })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.status === 'already_enrolled' ? 'Already enrolled!' : 'Successfully enrolled!');
        if (data.badges_earned?.length) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
        }
        fetchMyLearning();
      }
    } catch (err) {
      toast.error('Failed to enroll');
    }
  };

  const updateProgress = async (courseId, moduleId) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/foundation/courses/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          course_id: courseId, 
          module_id: moduleId,
          completed: true 
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Module completed!');
        if (data.badges_earned?.length) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
        }
        if (data.course_completed) {
          toast.success('Congratulations! Course completed!');
        }
        fetchMyLearning();
      }
    } catch (err) {
      toast.error('Failed to update progress');
    }
  };

  const getEnrollment = (courseId) => {
    if (!myLearning) return null;
    return [...(myLearning.in_progress || []), ...(myLearning.completed || [])]
      .find(e => e.course_id === courseId);
  };

  const filteredCourses = courses.filter(course => {
    if (filter.tier !== 'all' && course.access_tier !== filter.tier) return false;
    if (filter.level !== 'all' && course.level !== filter.level) return false;
    if (search && !course.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Course Detail View
  if (selectedCourse) {
    const enrollment = getEnrollment(selectedCourse.id);
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-indigo-50/30" data-testid="course-detail">
        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-900 to-purple-900 text-white py-12">
          <div className="container mx-auto px-4">
            <Link to="/foundation/school" className="inline-flex items-center text-indigo-200 hover:text-white mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Courses
            </Link>
            <div className="flex items-start gap-2 mb-3">
              <Badge className="bg-white/20">{selectedCourse.category}</Badge>
              <Badge variant={selectedCourse.access_tier === "free" ? "secondary" : "default"}>
                {selectedCourse.access_tier === "free" ? "Free" : "Premium"}
              </Badge>
            </div>
            <h1 className="text-3xl font-bold mb-3">{selectedCourse.title}</h1>
            <p className="text-indigo-100 max-w-2xl mb-4">{selectedCourse.description}</p>
            <div className="flex items-center gap-6 text-sm text-indigo-200">
              <span className="flex items-center"><Clock className="h-4 w-4 mr-1" /> {selectedCourse.duration_weeks} weeks</span>
              <span className="flex items-center"><Target className="h-4 w-4 mr-1" /> {selectedCourse.modules?.length} modules</span>
              <span className="flex items-center"><Star className="h-4 w-4 mr-1" /> {selectedCourse.level}</span>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Course Content */}
            <div className="lg:col-span-2 space-y-4">
              <h2 className="text-xl font-bold text-stone-800">Course Modules</h2>
              
              {selectedCourse.modules?.map((module, idx) => {
                const isCompleted = enrollment?.completed_modules?.includes(module.id);
                
                return (
                  <Card key={module.id} className={isCompleted ? "border-emerald-200 bg-emerald-50/50" : ""}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isCompleted ? 'bg-emerald-500 text-white' : 'bg-indigo-100 text-indigo-600'
                          }`}>
                            {isCompleted ? <Check className="h-5 w-5" /> : idx + 1}
                          </div>
                          <div>
                            <h3 className="font-medium text-stone-800">{module.title}</h3>
                            <p className="text-sm text-stone-500">{module.description}</p>
                            <span className="text-xs text-stone-400">{module.duration_minutes} min</span>
                          </div>
                        </div>
                        
                        {enrollment && !isCompleted && (
                          <Button 
                            size="sm"
                            onClick={() => updateProgress(selectedCourse.id, module.id)}
                            className="bg-indigo-600 hover:bg-indigo-700"
                          >
                            <Check className="h-4 w-4 mr-1" /> Complete
                          </Button>
                        )}
                        {isCompleted && (
                          <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Progress Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Your Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  {enrollment ? (
                    <div className="space-y-4">
                      <Progress value={enrollment.completion_percentage || 0} className="h-3" />
                      <p className="text-sm text-stone-600 text-center">
                        {Math.round(enrollment.completion_percentage || 0)}% Complete
                      </p>
                      <div className="text-center text-sm text-stone-500">
                        {enrollment.completed_modules?.length || 0} of {selectedCourse.modules?.length} modules
                      </div>
                    </div>
                  ) : (
                    <div className="text-center space-y-4">
                      <p className="text-stone-500 text-sm">
                        {user ? "Enroll to track your progress" : "Log in to enroll"}
                      </p>
                      <Button 
                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => enrollInCourse(selectedCourse.id)}
                        disabled={!user}
                      >
                        <Play className="mr-2 h-4 w-4" />
                        {user ? "Enroll Now" : "Log in to Enroll"}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Instructor */}
              {selectedCourse.instructor && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Instructor</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                        <GraduationCap className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium text-stone-800">{selectedCourse.instructor.name}</p>
                        <p className="text-sm text-stone-500">{selectedCourse.instructor.bio}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Badge Reward */}
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="py-6 text-center">
                  <Star className="h-10 w-10 text-amber-500 mx-auto mb-2" />
                  <p className="font-medium text-stone-800">Complete to earn</p>
                  <p className="text-amber-600 font-bold">{selectedCourse.badge_trigger} Badge</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Course List View
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-indigo-50/30" data-testid="virtual-school">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-900 to-purple-900 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/foundation" className="inline-flex items-center text-indigo-200 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Foundation
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <GraduationCap className="h-8 w-8" />
            <h1 className="text-3xl font-bold">24/7 Virtual School</h1>
          </div>
          <p className="text-indigo-100">Self-paced courses for mind, character, and leadership</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input 
              placeholder="Search courses..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <select 
            className="border rounded-md px-3 py-2 text-sm"
            value={filter.tier}
            onChange={(e) => setFilter({...filter, tier: e.target.value})}
          >
            <option value="all">All Tiers</option>
            <option value="free">Free</option>
            <option value="premium">Premium</option>
          </select>
          <select 
            className="border rounded-md px-3 py-2 text-sm"
            value={filter.level}
            onChange={(e) => setFilter({...filter, level: e.target.value})}
          >
            <option value="all">All Levels</option>
            <option value="foundational">Foundational</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        {/* My Learning Section */}
        {user && myLearning && (myLearning.in_progress?.length > 0 || myLearning.completed?.length > 0) && (
          <div className="mb-10">
            <h2 className="text-xl font-bold text-stone-800 mb-4">My Learning</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myLearning.in_progress?.map(enrollment => (
                <Card key={enrollment.course_id} className="border-indigo-200 bg-indigo-50/30">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-indigo-100 text-indigo-700">In Progress</Badge>
                      <span className="text-sm text-stone-500">{Math.round(enrollment.completion_percentage || 0)}%</span>
                    </div>
                    <h3 className="font-medium text-stone-800 mb-2">{enrollment.course_title}</h3>
                    <Progress value={enrollment.completion_percentage || 0} className="h-2 mb-3" />
                    <Link to={`/foundation/school/${enrollment.course_id}`}>
                      <Button size="sm" className="w-full">Continue Learning</Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* All Courses */}
        <h2 className="text-xl font-bold text-stone-800 mb-4">All Courses</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course, idx) => {
            const enrollment = getEnrollment(course.id);
            
            return (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={course.access_tier === "free" ? "secondary" : "default"}>
                        {course.access_tier === "free" ? "Free" : (
                          <span className="flex items-center"><Lock className="h-3 w-3 mr-1" /> Premium</span>
                        )}
                      </Badge>
                      <Badge variant="outline">{course.level}</Badge>
                    </div>
                    <CardTitle className="text-lg">{course.title}</CardTitle>
                    <CardDescription className="line-clamp-2">{course.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-stone-500 mb-4">
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {course.duration_weeks} weeks
                      </span>
                      <span className="flex items-center">
                        <Target className="h-4 w-4 mr-1" />
                        {course.modules?.length || 0} modules
                      </span>
                    </div>
                    
                    {enrollment ? (
                      <Link to={`/foundation/school/${course.id}`}>
                        <Button className="w-full" variant={enrollment.completed ? "outline" : "default"}>
                          {enrollment.completed ? (
                            <><Check className="mr-2 h-4 w-4" /> Review</>
                          ) : (
                            <><Play className="mr-2 h-4 w-4" /> Continue</>
                          )}
                        </Button>
                      </Link>
                    ) : (
                      <Link to={`/foundation/school/${course.id}`}>
                        <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                          <Play className="mr-2 h-4 w-4" />
                          Start Course
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
